import React from 'react';
import { createPortal } from 'react-dom';
import { useSlotPortal } from 'src/lib/hooks';

/**
 * Target for {@link PageHeadingSlot}. The div lives in `AuthenticatedHeader`, so page
 * breadcrumbs render in the sticky top row instead of taking their own row in the content
 * area (#2611).
 */
export const PAGE_HEADING_SLOT_ID = 'page-heading-slot';

/**
 * Portals a page's breadcrumb and `h1` into the sticky header.
 *
 * Children are wrapped in a single element so the slot can show only its newest occupant: when
 * a route re-suspends, React keeps the resolved tree mounted (hidden with `display:none`) and
 * mounts the fallback alongside it — but portalled children live in the header, outside that
 * hidden subtree, so both would render. The slot hides every occupant but the last, which is
 * the one React appended most recently. Without it a re-suspend stacks two breadcrumb trails
 * and puts two `h1`s in the accessibility tree.
 *
 * Keep exactly one `h1` per page state, as a sibling of the breadcrumb `<nav>` rather than
 * inside it — `BreadcrumbHeading` is that element.
 */
export const PageHeadingSlot: React.FC<React.PropsWithChildren> = ({ children }) => {
  const node = useSlotPortal(PAGE_HEADING_SLOT_ID);

  if (!node) {
    return null;
  }

  return createPortal(
    <div className={'tw-flex tw-min-w-0 tw-items-center tw-gap-2'}>{children}</div>,
    node,
  );
};
